import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material'
import { CarrierVisitPerformanceTerminalDto } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, useMinimalsTheme } from '@tom-ui/ui'
import { useDateFormat } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'

type PerformanceCardProps = {
  terminalPerformance?: CarrierVisitPerformanceTerminalDto | null
}

export const PerformanceCard: FC<PerformanceCardProps> = observer(({ terminalPerformance }) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const { formatMinutesToHoursAndMinutes } = useDateFormat()

  const getTableCellValue = (value?: number | null, label?: string) => {
    if (!value) {
      return EmptyIndicator
    }

    return `${value.toFixed(2)} ${label ?? ''}`
  }

  return (
    <Paper sx={{ padding: theme.customSpacing.m, height: '100%' }}>
      <Stack direction='row' gap={theme.customSpacing.m}>
        <Stack direction='column' width='50%'>
          <Typography
            variant='h6'
            paddingLeft={theme.customSpacing.m}
            marginBottom={theme.customSpacing.m}
          >
            {t('terminalPerformance', 'Terminal Performance')}
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table size='small' aria-label='a dense table'>
              <TableBody>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('avgGrossProductivity', 'Avg Gross Productivity')}{' '}
                  </TableCell>
                  <TableCell>
                    {getTableCellValue(
                      terminalPerformance?.avgGrossProductivity,
                      t('movesPerHour', 'moves/hour'),
                    )}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('berthProductivity', 'Berth productivity')}{' '}
                  </TableCell>
                  <TableCell>
                    {getTableCellValue(
                      terminalPerformance?.berthProductivity,
                      t('movesPerHourPerBerth', 'moves/hour per berth'),
                    )}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('realDayRate', 'Real day rate')}{' '}
                  </TableCell>
                  <TableCell>
                    {getTableCellValue(
                      terminalPerformance?.realDayRate,
                      t('movesPerHour', 'moves/hour'),
                    )}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('grossShipDay', 'Gross ship day')}{' '}
                  </TableCell>
                  <TableCell>
                    {getTableCellValue(
                      terminalPerformance?.grossShipDay,
                      t('movesPerDay', 'moves/day'),
                    )}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('craneIntensity', 'Crane intensity')}{' '}
                  </TableCell>
                  <TableCell>
                    {getTableCellValue(
                      terminalPerformance?.craneIntensity,
                      t('cranesPerVessel', 'cranes/vessel'),
                    )}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('avgNoCranes', 'Avg no of cranes')}{' '}
                  </TableCell>
                  <TableCell>
                    {terminalPerformance?.avgNumberCranes?.toFixed(2) ?? EmptyIndicator}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
        <Stack direction='column' width='50%'>
          <Typography
            variant='h6'
            paddingLeft={theme.customSpacing.m}
            marginBottom={theme.customSpacing.m}
          >
            {t('equipmentPerformance', 'Equipment performance')}
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table size='small' aria-label='a dense table'>
              <TableBody>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('netCraneRate', 'Net crane rate')}{' '}
                  </TableCell>
                  <TableCell>
                    {getTableCellValue(
                      terminalPerformance?.netCraneRate,
                      t('movesPerHourPerCrane', 'moves/hour per crane'),
                    )}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('gross CraneRate', 'Gross crane rate')}{' '}
                  </TableCell>
                  <TableCell>
                    {getTableCellValue(
                      terminalPerformance?.grossCraneRate,
                      t('movesPerHourPerCrane', 'moves/hour per crane'),
                    )}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component='th' scope='row'>
                    {t('netCraneHours', 'Net crane hours')}{' '}
                  </TableCell>
                  <TableCell>
                    {terminalPerformance?.netCraneMinutes
                      ? formatMinutesToHoursAndMinutes(terminalPerformance?.netCraneMinutes ?? 0)
                      : EmptyIndicator}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Stack>
    </Paper>
  )
})
